import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Col,
  Row,
  Card,
  Container,
  Button,
  Badge,
  Alert,
  Table,
  Spinner,
  Form,
} from "react-bootstrap";
import {
  fetchInventoryOverview,
  fetchLowStockProducts,
  fetchStockAdjustments,
  fetchPurchaseOrders,
  clearError,
  clearSuccess,
} from "../../redux/slices/inventorySlice";
import { FaBoxes, FaThLarge, FaChartLine, FaWarehouse } from "react-icons/fa";
import { fetchProducts } from "../../redux/slices/productSlice";
import inventoryService from "../../services/inventoryService";

const InventoryOverview = () => {
  const dispatch = useDispatch();
  const {
    inventoryOverview,
    lowStockProducts,
    stockAdjustments,
    purchaseOrders,
    isLoadingInventoryOverview,
    isLoadingLowStockProducts,
    error,
    success,
  } = useSelector((state) => state.inventory);

  const { products = [] } = useSelector((state) => state.products);

  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const [dateRange, setDateRange] = useState("7"); // days
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const [purchaseOrderItemsMap, setPurchaseOrderItemsMap] = useState({});

  useEffect(() => {
    dispatch(fetchInventoryOverview());
    dispatch(fetchProducts());
  }, [dispatch]);

  // Debounced fetch for low stock products on threshold change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Handle empty string or invalid values - use 0 for empty, 10 for invalid
      let validThreshold;
      if (
        lowStockThreshold === "" ||
        lowStockThreshold === null ||
        lowStockThreshold === undefined
      ) {
        validThreshold = 0;
      } else if (isNaN(lowStockThreshold) || lowStockThreshold < 0) {
        validThreshold = 10;
      } else {
        validThreshold = lowStockThreshold;
      }
      dispatch(fetchLowStockProducts(validThreshold));
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [dispatch, lowStockThreshold]);

  // Debug log for low stock products
  useEffect(() => {
    console.log("Low Stock Products:", lowStockProducts);
  }, [lowStockProducts]);

  useEffect(() => {
    const params = { days: parseInt(dateRange) };
    dispatch(fetchStockAdjustments(params));
    dispatch(fetchPurchaseOrders(params));
  }, [dispatch, dateRange]);

  // Fetch purchase items for recent orders
  useEffect(() => {
    async function loadItems() {
      if (!Array.isArray(purchaseOrders) || purchaseOrders.length === 0) {
        setPurchaseOrderItemsMap({});
        return;
      }
      const ordersToFetch = purchaseOrders.slice(0, 10);
      try {
        const results = await Promise.all(
          ordersToFetch.map(async (po) => {
            try {
              const res = await inventoryService.getPurchaseItems(po.id);
              const items = Array.isArray(res)
                ? res
                : res.data || res.items || [];
              return [po.id, items];
            } catch (e) {
              return [po.id, []];
            }
          })
        );
        const map = {};
        results.forEach(([orderId, items]) => {
          map[orderId] = items;
        });
        setPurchaseOrderItemsMap(map);
      } catch (e) {
        setPurchaseOrderItemsMap({});
      }
    }
    loadItems();
  }, [purchaseOrders]);

  // Clear messages after a delay
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const { formatDateToDDMMYYYY } = require("../../utils/dateUtils");
    return formatDateToDDMMYYYY(dateString) || "-";
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product ? product.name : `Product #${productId}`;
  };

  const getStockStatusBadge = (quantity, threshold = 10) => {
    if (quantity === 0) {
      return <Badge bg="danger">Out of Stock</Badge>;
    } else if (quantity <= threshold) {
      return <Badge bg="warning">Low Stock</Badge>;
    } else {
      return <Badge bg="success">In Stock</Badge>;
    }
  };

  const getAdjustmentTypeBadge = (type) => {
    return type === "increase" ? (
      <Badge bg="success">+</Badge>
    ) : (
      <Badge bg="danger">-</Badge>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      paid: { variant: "success", text: "Paid" },
      unpaid: { variant: "danger", text: "Unpaid" },
      partial: { variant: "warning", text: "Partial" },
    };
    const config = statusConfig[status] || {
      variant: "secondary",
      text: status,
    };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // Compute filtered low-stock list (only <= threshold, including out-of-stock)
  const safeThreshold =
    lowStockThreshold === "" ||
    lowStockThreshold === null ||
    lowStockThreshold === undefined
      ? 0
      : lowStockThreshold;
  const filteredLowStockProducts = Array.isArray(lowStockProducts)
    ? lowStockProducts.filter((p) => (p.stock_quantity || 0) <= safeThreshold)
    : [];

  return (
    <Container fluid className="py-2">
      {/* <Row>
        <Col>
          <h2 className="mb-4">Inventory Overview</h2>
        </Col>
      </Row> */}
      {/* <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">Inventory Overview</h5>
          </div> */}

      {/* Error and Success Messages */}
      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => dispatch(clearError())}
          className="mb-4"
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          dismissible
          onClose={() => dispatch(clearSuccess())}
          className="mb-4"
        >
          {success}
        </Alert>
      )}

      {/* Filters */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Inventory
        </Link>
      </div>

      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Low Stock Threshold</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={lowStockThreshold}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string for better UX while typing
                if (value === "") {
                  setLowStockThreshold("");
                } else {
                  const parsed = parseInt(value);
                  setLowStockThreshold(isNaN(parsed) ? "" : parsed);
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Date Range (Days)</Form.Label>
            <Form.Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Overview Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} xs={12}>
          {/* <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">
                {isLoadingInventoryOverview ? (
                  <Spinner size="sm" />
                ) : (
                  inventoryOverview?.totalProducts || 0
                )}
              </h3>
              <p className="mb-0">Total Products aaa</p>
            </Card.Body>
          </Card> */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <div
                  className="stat-icon star-icon-bg-color1" // style={{ backgroundColor: stat.color }}
                >
                  <FaBoxes />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">Total Products</h3>
                  <p className="stat-value stat-value-color1">
                    {isLoadingInventoryOverview ? (
                      <Spinner size="sm" />
                    ) : (
                      inventoryOverview?.totalProducts || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col md={3}>
          {/* <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">
                {isLoadingInventoryOverview ? (
                  <Spinner size="sm" />
                ) : (
                  inventoryOverview?.totalCategories || 0
                )}
              </h3>
              <p className="mb-0">Total Categories</p>
            </Card.Body>
          </Card> */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <div
                  className="stat-icon star-icon-bg-color2" // style={{ backgroundColor: stat.color }}
                >
                  <FaThLarge />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">Total Categories</h3>
                  <p className="stat-value stat-value-color2">
                    {isLoadingInventoryOverview ? (
                      <Spinner size="sm" />
                    ) : (
                      inventoryOverview?.totalCategories || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col md={3}>
          {/* <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">
                {isLoadingLowStockProducts ? (
                  <Spinner size="sm" />
                ) : (
                  lowStockProducts?.length || 0
                )}
              </h3>
              <p className="mb-0">Low Stock Products</p>
            </Card.Body>
          </Card> */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <div
                  className="stat-icon star-icon-bg-color3" // style={{ backgroundColor: stat.color }}
                >
                  <FaChartLine />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">Low Stock Prods</h3>
                  <p className="stat-value stat-value-color3">
                    {isLoadingLowStockProducts ? (
                      <Spinner size="sm" />
                    ) : (
                      filteredLowStockProducts.length || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col md={3}>
          {/* <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">
                {isLoadingInventoryOverview ? (
                  <Spinner size="sm" />
                ) : (
                  inventoryOverview?.totalQuantity || 0
                )}
              </h3>
              <p className="mb-0">Total Quantity</p>
            </Card.Body>
          </Card> */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <div
                  className="stat-icon star-icon-bg-color4" // style={{ backgroundColor: stat.color }}
                >
                  <FaWarehouse />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">Total Quantity</h3>
                  <p className="stat-value stat-value-color4">
                    {isLoadingInventoryOverview ? (
                      <Spinner size="sm" />
                    ) : (
                      inventoryOverview?.totalQuantity || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        {/* Low Stock Products */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0">Low Stock Products</h5>
                {/* <small className="text-muted">(≤ {lowStockThreshold})</small> */}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="warning">{filteredLowStockProducts.length}</Badge>
                {/* <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowAllLowStock((v) => !v)}
                >
                  {showAllLowStock ? "Show Top 5" : "Show All"}
                </Button> */}
              </div>
            </Card.Header>
            <Card.Body>
              {isLoadingLowStockProducts ? (
                <div className="text-center py-3">
                  <Spinner size="sm" />
                </div>
              ) : filteredLowStockProducts.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover size="sm">
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>Current Stock</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllLowStock
                        ? filteredLowStockProducts
                        : filteredLowStockProducts.slice(0, 5)
                      ).map((product) => (
                        <tr key={product.id}>
                          <td>{product.name || "Unknown Product"}</td>
                          <td>
                            {product.stock_quantity || 0} {product.unit || ""}
                          </td>
                          <td>
                            {getStockStatusBadge(
                              product.stock_quantity,
                              lowStockThreshold
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {!showAllLowStock && filteredLowStockProducts.length > 5 && (
                    <div className="text-center">
                      <small className="text-muted">
                        Showing 5 of {filteredLowStockProducts.length} products
                      </small>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-3 text-muted">
                  No low stock products found
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Stock Adjustments */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent Stock Adjustments</h5>
              <Badge bg="info">
                {Array.isArray(stockAdjustments)
                  ? Math.min(stockAdjustments.length, 5)
                  : 0}
              </Badge>
            </Card.Header>
            <Card.Body>
              {Array.isArray(stockAdjustments) &&
              stockAdjustments.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover size="sm">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Type</th>
                        <th>Qty</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockAdjustments.slice(0, 5).map((adjustment) => (
                        <tr key={adjustment.id}>
                          <td>{getProductName(adjustment.product_id)}</td>
                          <td>
                            {getAdjustmentTypeBadge(adjustment.adjustment_type)}
                          </td>
                          <td>{adjustment.quantity}</td>
                          <td>{formatDate(adjustment.date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {stockAdjustments.length > 5 && (
                    <div className="text-center">
                      <small className="text-muted">
                        Showing 5 of {stockAdjustments.length} adjustments
                      </small>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-3 text-muted">
                  No recent stock adjustments
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Recent Purchase Items */}
        <Col md={12} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent Purchase Items</h5>
              <Badge bg="primary">
                {(() => {
                  if (
                    !Array.isArray(purchaseOrders) ||
                    purchaseOrders.length === 0
                  ) {
                    return 0;
                  }
                  // Collect all items from purchase orders
                  const allItems = purchaseOrders.flatMap((order) => {
                    const items = purchaseOrderItemsMap[order.id] || [];
                    if (items.length === 0) {
                      // Return a placeholder for orders with no items
                      return [
                        {
                          orderId: order.id,
                          vendor_name:
                            order.vendor_name || `Vendor #${order.vendor_id}`,
                          payment_status: order.payment_status,
                          purchase_date: order.purchase_date,
                          isEmpty: true,
                        },
                      ];
                    }
                    return items.map((it) => ({
                      ...it,
                      orderId: order.id,
                      vendor_name:
                        order.vendor_name || `Vendor #${order.vendor_id}`,
                      payment_status: order.payment_status,
                      purchase_date: order.purchase_date,
                      isEmpty: false,
                    }));
                  });
                  return Math.min(allItems.length, 10);
                })()}
              </Badge>
            </Card.Header>
            <Card.Body>
              {Array.isArray(purchaseOrders) && purchaseOrders.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Date</th>
                        <th>Quantity</th>
                        <th>Purchase Price</th>
                        <th>Total Price</th>
                        <th>Payment Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Collect all items from purchase orders
                        const allItems = purchaseOrders.flatMap((order) => {
                          const items = purchaseOrderItemsMap[order.id] || [];
                          if (items.length === 0) {
                            // Return a placeholder for orders with no items
                            return [
                              {
                                orderId: order.id,
                                vendor_name:
                                  order.vendor_name ||
                                  `Vendor #${order.vendor_id}`,
                                payment_status: order.payment_status,
                                purchase_date: order.purchase_date,
                                isEmpty: true,
                              },
                            ];
                          }
                          return items.map((it) => ({
                            ...it,
                            orderId: order.id,
                            vendor_name:
                              order.vendor_name || `Vendor #${order.vendor_id}`,
                            payment_status: order.payment_status,
                            purchase_date: order.purchase_date,
                            isEmpty: false,
                          }));
                        });

                        // Limit to 10 items
                        const displayedItems = allItems.slice(0, 10);
                        const totalItems = allItems.length;

                        return (
                          <>
                            {displayedItems.map((item, idx) =>
                              item.isEmpty ? (
                                <tr key={`${item.orderId}-empty`}>
                                  <td colSpan={5} className="text-muted">
                                    No items
                                  </td>
                                  <td>
                                    {getPaymentStatusBadge(item.payment_status)}
                                  </td>
                                </tr>
                              ) : (
                                <tr key={`${item.orderId}-${idx}`}>
                                  <td>
                                    {item.product_name || item.product_id}
                                  </td>
                                  <td>{formatDate(item.purchase_date)}</td>
                                  <td>{item.quantity_purchased}</td>
                                  <td>
                                    {formatCurrency(
                                      item.purchase_price_per_unit
                                    )}
                                  </td>
                                  <td>{formatCurrency(item.total_price)}</td>
                                  <td>
                                    {getPaymentStatusBadge(item.payment_status)}
                                  </td>
                                </tr>
                              )
                            )}
                            {totalItems > 10 && (
                              <tr>
                                <td colSpan={6} className="text-center">
                                  <small className="text-muted">
                                    Showing 10 of {totalItems} purchase items
                                  </small>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-3 text-muted">
                  No recent purchase orders
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InventoryOverview;
